import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';

import {
    COLOR_BLUE,
    COLOR_GREY_DARK,
    COLOR_OUTLINE,
    COLOR_TEAL,
    COLOR_TEAL_SURFACE,
    COLOR_WHITE
} from '../../../theme';
import type { WatchlistMatchMode } from '../../../types';
import MatchModeIllustration from '../MatchModeIllustration';

type OptionType = {
    value: WatchlistMatchMode,
    title: string,
    help: string
};

const OPTIONS: OptionType[] = [
    {
        value: 'within',
        title: 'Hela uppdraget inom området',
        help: 'Både upphämtning och leverans ligger i området.'
    },
    {
        value: 'touches',
        title: 'Hämtas eller lämnas i området',
        help: 'Räcker att upphämtning eller leverans ligger i området.'
    }
];

type PropsType = {
    value: WatchlistMatchMode,
    onChange: (mode: WatchlistMatchMode) => void,
    label: string,
    /** Smaller cards without the help text (for popovers). */
    compact?: boolean
};

/** Two clickable picture cards ("Hela uppdraget" / "Hämtas eller lämnas") acting as a radio group. */
export default function MatchModePicker({
    value, onChange, label, compact = false
}: PropsType) {
    return (
        <Box
            role="radiogroup"
            aria-label={label}
            sx={{
                display: 'grid',
                gridTemplateColumns: compact ? '1fr 1fr' : { xs: '1fr', sm: '1fr 1fr' },
                gap: compact ? 1 : 1.5
            }}
        >
            {OPTIONS.map((o) => {
                const selected = value === o.value;
                return (
                    <Box
                        key={o.value}
                        component="button"
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        title={compact ? o.help : undefined}
                        onClick={() => onChange(o.value)}
                        sx={{
                            position: 'relative',
                            p: compact ? 0.75 : 1.5,
                            textAlign: 'left',
                            fontFamily: 'inherit',
                            cursor: 'pointer',
                            borderRadius: 2,
                            border: `2px solid ${selected ? COLOR_TEAL : alpha(COLOR_OUTLINE, 0.5)}`,
                            bgcolor: selected ? COLOR_TEAL_SURFACE : COLOR_WHITE,
                            transition: 'background-color 0.15s ease, border-color 0.15s ease',
                            '&:hover': { borderColor: COLOR_TEAL },
                            '&:focus-visible': { outline: `2px solid ${COLOR_TEAL}`, outlineOffset: 2 }
                        }}
                    >
                        {selected && (
                            <CheckCircleIcon
                                fontSize="small"
                                sx={{
                                    position: 'absolute', top: compact ? 4 : 8, right: compact ? 4 : 8, color: COLOR_TEAL
                                }}
                            />
                        )}
                        <Box sx={{ bgcolor: COLOR_WHITE, borderRadius: 1, border: `1px solid ${COLOR_TEAL_SURFACE}` }}>
                            <MatchModeIllustration mode={o.value} />
                        </Box>
                        <Typography sx={{
                            mt: compact ? 0.5 : 1,
                            fontWeight: 700,
                            color: COLOR_BLUE,
                            lineHeight: 1.3,
                            fontSize: compact ? 12 : undefined
                        }}
                        >
                            {o.title}
                        </Typography>
                        {!compact && (
                            <Typography variant="body2" sx={{ color: COLOR_GREY_DARK, mt: 0.25 }}>
                                {o.help}
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
